import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import DataTable from "../components/common/DataTable";
import Button from "../components/common/Button";
import Badge from "../components/common/Badge";
import Modal from "../components/common/Modal";
import { deleteUserByAdmin, listUsers, revokeAdmin } from "../services/userService";
import { listAccessRequests, reviewAccessRequest } from "../services/accessRequestService";
import { useAuth } from "../contexts/AuthContext";
import { formatDateTime } from "../utils/format";
import { useToast } from "../contexts/ToastContext";

export default function AdminUsersPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { push } = useToast();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAdminCode, setDeleteAdminCode] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const load = async () => {
    const [allUsers, allRequests] = await Promise.all([listUsers(), listAccessRequests()]);
    setUsers(allUsers);
    setRequests(allRequests);
  };

  useEffect(() => {
    load();
  }, []);

  const pendingCount = useMemo(() => requests.filter((r) => r.status === "pending").length, [requests]);
  const pending = useMemo(() => requests.filter((r) => r.status === "pending"), [requests]);
  const completed = useMemo(() => requests.filter((r) => r.status !== "pending"), [requests]);
  const adminCount = useMemo(() => users.filter((u) => u.role === "admin").length, [users]);

  return (
    <AppShell title="User Management" pendingCount={pendingCount}>
      <div className="control-row" style={{ marginBottom: 12 }}>
        <Button variant={tab === "users" ? "primary" : "secondary"} onClick={() => setTab("users")}>
          All Users
        </Button>
        <Button variant={tab === "requests" ? "primary" : "secondary"} onClick={() => setTab("requests")}>
          Access Requests
        </Button>
      </div>
      {tab === "users" ? (
        <Card>
          <DataTable
            columns={[
              { key: "displayName", label: "Name" },
              { key: "email", label: "Email" },
              {
                key: "role",
                label: "Role",
                render: (u) => <Badge tone={u.role === "admin" ? "admin" : "standard"}>{u.role}</Badge>,
              },
              { key: "createdAt", label: "Joined", render: (u) => formatDateTime(u.createdAt) },
              { key: "lastLogin", label: "Last Login", render: (u) => formatDateTime(u.lastLogin) },
              {
                key: "actions",
                label: "Actions",
                render: (u) => (
                  <div className="inline-actions">
                    {u.role === "admin" ? (
                      <Button
                        variant="danger"
                        disabled={u.uid === user.uid || adminCount <= 1}
                        title={
                          u.uid === user.uid
                            ? "You cannot revoke your own admin role."
                            : adminCount <= 1
                              ? "At least one admin must remain."
                              : ""
                        }
                        onClick={async () => {
                          try {
                            await revokeAdmin(
                              u.uid,
                              { uid: user.uid, email: user.email, displayName: profile.displayName },
                              { displayName: u.displayName }
                            );
                            push("Admin revoked", "info");
                            load();
                            await refreshProfile();
                          } catch (err) {
                            if (err?.code === "user/cannot-revoke-self") {
                              push("You cannot revoke your own admin role.", "error");
                            } else if (err?.code === "user/cannot-revoke-last-admin") {
                              push("At least one admin account must remain.", "error");
                            } else {
                              push("Could not revoke admin access.", "error");
                            }
                          }
                        }}
                      >
                        Revoke Admin
                      </Button>
                    ) : null}
                    <Button
                      variant="danger"
                      disabled={u.uid === user.uid}
                      title={u.uid === user.uid ? "You cannot delete your own account here." : "Permanently delete this user and their data."}
                      onClick={() => {
                        setDeleteAdminCode("");
                        setDeleteTarget(u);
                      }}
                    >
                      Delete account
                    </Button>
                  </div>
                ),
              },
            ]}
            rows={users}
            empty={<p className="muted">No users found.</p>}
          />
        </Card>
      ) : (
        <div className="container-grid">
          <Card>
            <h2 className="h2">Pending Requests</h2>
            <DataTable
              columns={[
                { key: "displayName", label: "Name" },
                { key: "email", label: "Email" },
                { key: "reason", label: "Reason" },
                { key: "requestedAt", label: "Requested", render: (r) => formatDateTime(r.requestedAt) },
                { key: "status", label: "Status", render: () => <Badge tone="pending">Pending</Badge> },
                {
                  key: "actions",
                  label: "Actions",
                  render: (r) => (
                    <div className="inline-actions">
                      <Button
                        onClick={async () => {
                          await reviewAccessRequest(r, { uid: user.uid, email: user.email, displayName: profile.displayName }, true);
                          push("Request approved", "success");
                          load();
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => setRejectTarget(r)}
                      >
                        Reject
                      </Button>
                    </div>
                  ),
                },
              ]}
              rows={pending}
              empty={<p className="muted">No pending requests.</p>}
            />
          </Card>
          <Card>
            <h2 className="h2">Completed Requests</h2>
            <DataTable
              columns={[
                { key: "displayName", label: "Name" },
                { key: "email", label: "Email" },
                { key: "reason", label: "Reason" },
                { key: "requestedAt", label: "Requested", render: (r) => formatDateTime(r.requestedAt) },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => <Badge tone={r.status === "approved" ? "success" : "error"}>{r.status}</Badge>,
                },
                { key: "reviewedByName", label: "Reviewed By", render: (r) => r.reviewedByName || r.reviewedBy || "-" },
                { key: "reviewedAt", label: "Review Date", render: (r) => formatDateTime(r.reviewedAt) },
              ]}
              rows={completed}
              empty={<p className="muted">No completed requests.</p>}
            />
          </Card>
        </div>
      )}
      {rejectTarget ? (
        <Modal
          title="Reject access request?"
          danger
          confirmLabel="Reject"
          onClose={() => setRejectTarget(null)}
          onConfirm={async () => {
            await reviewAccessRequest(rejectTarget, { uid: user.uid, email: user.email, displayName: profile.displayName }, false);
            push("Request rejected", "info");
            setRejectTarget(null);
            load();
          }}
        >
          <p>This cannot be undone.</p>
        </Modal>
      ) : null}
      {deleteTarget ? (
        <Modal
          title="Delete user account?"
          danger
          confirmDisabled={deleteSubmitting}
          confirmLabel={deleteSubmitting ? "Deleting…" : "Delete permanently"}
          onClose={() => {
            if (!deleteSubmitting) {
              setDeleteTarget(null);
              setDeleteAdminCode("");
            }
          }}
          onConfirm={async () => {
            if (!deleteAdminCode.trim()) {
              push("Enter the admin access code to confirm.", "error");
              return;
            }
            setDeleteSubmitting(true);
            try {
              await deleteUserByAdmin(deleteTarget.uid, deleteAdminCode);
              push("User account deleted.", "success");
              setDeleteTarget(null);
              setDeleteAdminCode("");
              load();
              await refreshProfile();
            } catch (err) {
              const code = err?.code || "";
              const msg = err?.message || "Could not delete account.";
              if (code === "functions/permission-denied") {
                push(msg.includes("Invalid") ? "Invalid admin access code." : msg, "error");
              } else if (code === "functions/failed-precondition") {
                push(msg, "error");
              } else if (code === "functions/not-found") {
                push("User was not found.", "error");
              } else if (code === "functions/invalid-argument") {
                push(msg, "error");
              } else {
                push("Could not delete account.", "error");
              }
            } finally {
              setDeleteSubmitting(false);
            }
          }}
        >
          <p style={{ marginBottom: 12 }}>
            This removes <strong>{deleteTarget.displayName || deleteTarget.email}</strong> from Authentication and deletes their Firestore data, uploads, and
            requests. This cannot be undone.
          </p>
          <label className="muted" style={{ display: "block", marginBottom: 6 }}>
            Admin access code
          </label>
          <input
            type="password"
            autoComplete="off"
            value={deleteAdminCode}
            onChange={(e) => setDeleteAdminCode(e.target.value)}
            placeholder="Same code used for admin signup"
            style={{ width: "100%", maxWidth: 360 }}
            disabled={deleteSubmitting}
          />
        </Modal>
      ) : null}
    </AppShell>
  );
}
